<script setup>
import { useDisplay } from "vuetify"
import { useEventStore } from "@/stores/EventsStore"
import { extractDate } from "@/utils/datetime"
import { computed, ref } from "vue"
import Payment from "@/components/event/Payment.vue"
import PointsDisplayer from "@/components/PointsDisplayer.vue"
import { blockMinusInput, formatNumber, getInitials } from "@/utils/strings"
import ProductsSelector from "@/components/event/ProductsSelector.vue"
import ServicesSelectorPrimary from "@/components/event/ServicesSelectorPrimary.vue"
import Prepayment from "@/components/event/Prepayment.vue"
import { VForm } from "vuetify/lib/components/VForm/index"
import DateAndTimeSelector from "@/components/event/DateAndTimeSelector.vue"
import CustomerSelector from "@/components/event/CustomerSelector.vue"
import CustomerVouchersAndTickets from "@/components/event/CustomerVouchersAndTickets.vue"
import NumberInput from "@/components/NumberInput.vue"
import SectionDivider from "@/components/SectionDivider.vue"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import LoyaltyProgramHandler from "@/components/LoyaltyProgramHandler.vue"
import PanelTitleDivider from "@/components/PanelTitleDivider.vue"

/* ---------------------------- 👉 Props ---------------------------- */
const props = defineProps({
  jumpToDateFc: Function,
})


const eventStore = useEventStore()
const calendarStore = useCalendarStore()
const customerStore = useCustomerStore()

const { mobile } = useDisplay()

const isNormalVisit = computed(() => eventStore.selectedEvent?.type?.toString() === "0")

const vouchersCollapse = ref()
</script>

<template>
  <VSheet>
    <VRow class="mb-4 ">
      <VCol
        cols="12"
        :lg="eventStore.showFinalization ? 4 : 6"
        class="pr-lg-6"
        :class="mobile ? 'pt-0' : eventStore.showFinalization ? 'pseudo-right-divider' : ''"
      >
        <VForm ref="refForm">
          <SectionDivider
            :text="$t('basic_information')"
            class="mt-0"
          />
          <VRow
            v-if="isNormalVisit && eventStore.allowSave"
            dense
            justify="space-between"
            class="mb-4"
          />

          <!-- 👉 Selecting date, start, end time -->
          <SalesDateAndTimeSelector :jump-to-date-fc="jumpToDateFc" />

          <!-- 👉 Selecting client, customer card, adding new client -->
          <CustomerSelector :show-checkboxes="false" />


          <!--          <ServicesSelectorPrimary v-if="isNormalVisit" />-->
          <!-- 👉 Description -->
          <VRow>
            <VCol cols="12">
              <VTextarea
                v-model="eventStore.selectedEvent.description"
                :disabled="eventStore.isSaving"
                :readonly="!eventStore.allowSave"
                color="secondary"
                :label="$t('description')"
                :placeholder="$t('actions.form.fill_field')"
                :rows="isNormalVisit ? 1 : 6"
                hide-details
                density="compact"
              />
            </VCol>
          </VRow>
        </VForm>

        <!--Customer vouchers and tickets table DESKTOP -->
        <div v-if="customerStore.customerVouchersAndTickets?.length > 0 && !mobile">
          <VExpansionPanels
            v-model="vouchersCollapse"
            flat
            class="custom-collapse selected-products-collapse my-4"
          >
            <VExpansionPanel value="show">
              <PanelTitleDivider
                :text="$t('vouchersAndTickets')"
                :allow-badge="mobile || customerStore.customerVouchersAndTickets?.length > 0"
                :badge-content="Number(customerStore.customerVouchersAndTickets?.length)"
              />
              <VExpansionPanelText>
                <CustomerVouchersAndTickets
                  allow-voucher-use
                  :allow-pass-use="false"
                />
              </VExpansionPanelText>
            </VExpansionPanel>
          </VExpansionPanels>
        </div>
      </VCol>
      <VCol
        cols="12"
        :lg="eventStore.showFinalization ? 5 : 6"
        class="px-lg-6 pseudo-right-divider"
      >
        <SectionDivider
          :text="$t('manageProducts')"
          :class="mobile ? 'my-6' : 'mb-4'"
        />

        <ProductsSelector />
      </VCol>
      <VCol
        v-if="eventStore.showFinalization"
        cols="12"
        lg="3"
        :class="!mobile ? 'pl-6 ' : ''"
      >
        <!--Customer vouchers and tickets MOBILE-->
        <div v-if="customerStore.customerVouchersAndTickets?.length > 0 && mobile">
          <VExpansionPanels
            v-model="vouchersCollapse"
            flat
            class="custom-collapse selected-products-collapse mb-4 mt-0"
          >
            <VExpansionPanel value="show">
              <PanelTitleDivider
                :text="$t('vouchersAndTickets')"
                :allow-badge="mobile || customerStore.customerVouchersAndTickets?.length > 0"
                :badge-content="Number(customerStore.customerVouchersAndTickets?.length)"
              />
              <VExpansionPanelText>
                <CustomerVouchersAndTickets
                  allow-voucher-use
                  :allow-pass-use="false"
                />
              </VExpansionPanelText>
            </VExpansionPanel>
          </VExpansionPanels>
        </div>

        <SectionDivider
          :text="$t('payments')"
          class="mt-0 mb-1"
        />
        <div
          class="finalization-summary d-flex flex-column mb-4"
          :class="mobile ? 'my-6' : ''"
        >
          <VNumberInput
            v-if="eventStore.allowSave"
            v-model="eventStore.tipAmount"
            variant="outlined"
            color="secondary"
            density="compact"
            :label="$t('tip')"
            :suffix="calendarStore.currency"
            class="my-3"
            hide-details
            control-variant="hidden"
            maxlength="4"
            :min="0"
            :max="9999"
            :precision="2"
            :min-fraction-digits="0"
            @beforeinput="blockMinusInput"
          />
          <div class="d-flex justify-start flex-wrap ga-2 mb-4 mb-md-2">
            <div v-if="eventStore.totalDiscountAmount > 0">
              {{ $t('totalDiscounts') }}
              <span
                class="font-weight-bold"
                data-testid="total-discount"
              >
                {{ formatNumber(eventStore.totalDiscountAmount) }} {{ calendarStore.currency }}
              </span>
            </div>
          </div>
          <div class="d-flex align-center ga-2 mb-2">
            <p class="mb-0 text-h5">
              {{ $t('receivable') }}:
              <span
                :style="eventStore.finalPrice < eventStore.finalPriceWithNoDiscount ? 'text-decoration: line-through' : ''"
                data-testid="paymentsSection-ToBePaid-total"
              >
                {{ formatNumber(eventStore.finalPriceWithNoDiscount) }} {{ calendarStore.currency }}
              </span>
              <span
                v-if="eventStore.finalPrice < eventStore.finalPriceWithNoDiscount"
                class="ml-2 text-green font-weight-bold"
              >
                {{ formatNumber(eventStore.finalPrice) }} {{ calendarStore.currency }}
                <span class="mr-2">
                  {{ `(-${formatNumber(eventStore.selectedLoyaltyCoupon.value)}${eventStore.selectedLoyaltyCoupon?.discountType === 'PERCENT' ? '%' : calendarStore.currency})` }}
                </span>
              </span>


              <span class="ml-1">
                <VIcon
                  color="primary"
                  size="x-small"
                  class="mt-n1"
                  icon="ri-information-line"
                />
                <VTooltip
                  activator="parent"
                  :text="$t('price_info')"
                />
              </span>
            </p>
          </div>

          <Payment :is-in-sales="true" />
          <SectionDivider
            :text="$t('settlement')"
            class="mt-0 mb-4"
          />
          <VRow>
            <VCol cols="5">
              <div class="d-flex flex-column ga-2">
                <span>
                  {{ $t('paid') }}:
                </span>
                <span
                  class="text-h5"
                  :class="{ 'text-green': eventStore.balanceAmount === 0 }"
                  data-testid="paymentsSection-Paid-total"
                >
                  {{ formatNumber(eventStore.preAndPaymentMethodsTotal) }} {{ calendarStore.currency }}
                </span>
              </div>
            </VCol>
            <VSpacer />
            <VCol
              v-if="eventStore.allowSave"
              cols="6"
            >
              <div class="d-flex flex-column ga-2">
                <span v-if="eventStore.balanceAmount > 0">{{ $t('balanceAmount') }}:</span>
                <span v-else>{{ $t('remaining_amount') }}:</span>
                <span
                  class="text-h5"
                  :class="{ 'text-error': eventStore.balanceAmount > 0 }"
                >
                  <span
                    :class="{
                      'text-success': eventStore.balanceAmount > 0,
                      'text-error': eventStore.balanceAmount < 0
                    }"
                  >
                    {{ formatNumber(eventStore.balanceAmount) }} {{ calendarStore.currency }}

                  </span>
                </span>
              </div>
            </VCol>
            <VCol
              v-else
              cols="5"
            >
              <div class="d-flex flex-column ga-2">
                <span>
                  w tym napiwek:
                </span>
                <span class="text-h5 font-weight-thin">
                  {{ formatNumber(eventStore.tipAmount) }} {{ calendarStore.currency }}
                </span>
              </div>
            </VCol>
          </VRow>

          <VProgressLinear
            v-if="eventStore.allowSave"
            :model-value="eventStore.preAndPaymentMethodsTotal ? (eventStore.preAndPaymentMethodsTotal * 100 / eventStore.finalPrice) : 0"
            color="primary"
            height="14"
            :max="eventStore.preAndPaymentMethodsTotal === 0 ? 0 : 100"
            class="my-4"
          />

          <!-- Loyalty program -->
          <LoyaltyProgramHandler />
        </div>
      </VCol>
    </VRow>
  </VSheet>
</template>

<style lang="scss">
.break-spaces {
  white-space: break-spaces;
  line-height: normal;
}
.product-selector-container {
  .v-list-item-subtitle {
    -webkit-line-clamp: 2 !important;
  }
}
</style>
